import { lazy, Suspense, useEffect, useLayoutEffect, useRef, useState, type CSSProperties, type ReactNode } from 'react'
import { createPortal } from 'react-dom'
import { useQueries, useQuery } from '@tanstack/react-query'
import { Chatv1Error, createPage, deletePage, listPages, renamePage, setPageLock, type EditMode, type PageInfo } from '../chatv1/transport'
import { chatv1SelfUser } from '../chatv1/auth'
import { getProjectMembers, type ProjectRow } from '../transport/api'
import { Icon } from '../ui/Icon'
import { activityText, usePhone } from '../board/BoardScreen'
import { useDrawerCovers, useDrawerSwipe, useFocusBack } from '../board/BoardDrawer'
import { BoardChat, useRoomChat } from '../board/BoardChat'
import { EditLock, mayChange } from '../board/EditLock'
import { mentionRowsOfMembers } from '../agents/AgentMention'
import { agentPage, lastPage, openPageSel, openPages, onPageNav, rememberPage, selectPage, usePage, usePages } from './pages'
import './page.css'

/** The editor is the heaviest thing here (ProseMirror and BlockNote): it loads when a page is first opened. */
const PageEditor = lazy(() => import('./PageEditor'))

/** What a new page is called until its title is written (Notion's word). */
const UNTITLED = 'Untitled'

function failText(e: unknown): string {
  const code = e instanceof Chatv1Error ? e.code : ''
  if (code === 'too_many_pages') return 'This project has 200 pages — delete one first.'
  if (code === 'invalid_name') return 'A title is one line of up to 80 characters.'
  if (code === 'not_a_member') return 'You are no longer in this project.'
  if (code === 'no_such_page') return 'That page is no longer in this project.'
  return 'That did not save — check the connection and try again.'
}

/**
 * THE ROOM'S PAGES — owner, 2026-11-02: 讓便條貼顯示 Markdown 文本 … 就如同我開給你看的 Notion 頁面一樣. 把 Notion 這個
 * 頁面編輯的功能照抄過來 … (還能夠跟 Agents 共同編輯, 以及與朋友在 Project Chat 裡面協作).
 *
 * Over the whole app, as the room's whiteboard is: the room's pages at the left (Notion's sidebar), the open page beside
 * them — its title, then its blocks. Everyone in the room writes the same page, live. On a phone, the list or the page.
 * Reached by a link (`?page=<conv>&pg=<page>`, pages.ts); the room's own way in is its drawer (PagesDrawer).
 */
export function PageScreen({ convId, title, onClose }: { convId: string; title: string; onClose: () => void }) {
  const [sel, setSel] = useState(openPageSel)
  useEffect(() => onPageNav(() => setSel(openPageSel())), [])
  return createPortal(<PagesView convId={convId} title={title} sel={sel} onSelect={selectPage} onClose={onClose} />, document.body)
}

/**
 * THE ROOM'S PAGES, AS A DRAWER — owner, 2026-11-02: 幫我將 白板, Pages (筆記本). 都放在 Bookmark 的上方 … 然後手機窄版頁面
 * 也用抽屜打開的方式. From the room's column of corner buttons (its handle on a phone), the pages come in from the right
 * edge over the room — the rail beside them on a wide screen, the whole screen on a phone — on the page this device had
 * open there last. Which page is open is the drawer's own (not the address: a refresh lands back in the room).
 */
export function PagesDrawer({ convId, title, page, onClose }: { convId: string; title: string; page?: string | null; onClose: () => void }) {
  const covers = useDrawerCovers()
  useFocusBack()
  // A page a chat message named (its chip), else the one this device had open here last.
  const [sel, setSel] = useState(() => page ?? lastPage(convId))
  const swipe = useDrawerSwipe(onClose)
  return (
    <PagesView
      convId={convId}
      title={title}
      sel={sel}
      onSelect={(p) => {
        rememberPage(convId, p)
        setSel(p)
      }}
      onClose={onClose}
      drawer={{ covers, swipe }}
    />
  )
}

/**
 * THE AGENT'S PAGES, IN ITS SESSION — owner, 2026-11-03: 在 Agent session 新增一個 page icon (如同 whiteboard icon). 讓使用者
 * 能夠直接從 Agent session 裡面瀏覽/打開 project chat 裡的 page.
 *
 * The pages of a room the agent is in, over its conversation, as its whiteboard drawer is (board/BoardDrawer): the
 * session on a wide screen, the rail beside it; the whole screen on a phone. It opens on the page the agent wrote to last
 * (pages.ts agentPage) — chosen once, never moved under the person by the agent's next write; the room's name picks
 * another room when the agent is in several. On a wide screen it opens full screen too, where Back comes back here.
 */
export function AgentPagesDrawer({ hash, rooms, onClose }: { hash: string; rooms: ProjectRow[]; onClose: () => void }) {
  const covers = useDrawerCovers()
  const phone = usePhone()
  useFocusBack()
  // The rooms' lists, under the key the open page's own list reads (usePages): one read, and every frame keeps it.
  const qs = useQueries({
    queries: rooms.map((r) => ({
      queryKey: ['pages', r.project_id],
      queryFn: async () => (await listPages(r.project_id)).pages,
      staleTime: 15_000,
    })),
  })
  const settled = qs.every((q) => !q.isLoading)
  const [at, setAt] = useState<{ conv: string; page: string | null } | null>(null)
  const here = at && rooms.some((r) => r.project_id === at.conv) ? at : null
  const ids = rooms.map((r) => r.project_id)
  const choice = settled && !here ? agentPage(ids, qs.map((q) => q.data), hash, lastPage) : null
  const choiceConv = choice?.conv
  const choicePage = choice?.page
  useEffect(() => {
    if (choiceConv) setAt({ conv: choiceConv, page: choicePage ?? null })
  }, [choiceConv, choicePage])
  const shown = here ?? choice
  const room = rooms.find((r) => r.project_id === shown?.conv)
  const name = (r: ProjectRow) => r.name || 'Project'
  const swipe = useDrawerSwipe(onClose)

  if (!shown || !room) {
    return (
      <div className={['pg-screen pg-drawer', covers ? 'pg-cover' : '', phone ? 'pg-phone' : ''].filter(Boolean).join(' ')} role="complementary" aria-label="Pages">
        <div className="pg-top" {...swipe}>
          <button type="button" className="pg-back pg-drawer-close" onClick={onClose} aria-label="Close the pages" title="Close">
            <Icon name="chevron_right" size={22} />
          </button>
          <div className="pg-where">
            <Icon name="description" size={18} />
            <span className="pg-where-name">Pages</span>
          </div>
        </div>
        <p className="bd-drawer-note">{settled ? 'This agent is not in a project.' : 'Opening the pages…'}</p>
      </div>
    )
  }
  return (
    <PagesView
      key={shown.conv}
      convId={shown.conv}
      title={name(room)}
      sel={shown.page}
      onSelect={(p) => {
        rememberPage(shown.conv, p)
        setAt({ conv: shown.conv, page: p })
      }}
      onClose={onClose}
      drawer={{ covers, swipe }}
      room={
        rooms.length > 1 ? (
          <select
            className="bd-room-pick"
            aria-label="Project"
            value={shown.conv}
            onChange={(e) => {
              // The agent's page in that room, as on opening.
              const i = ids.indexOf(e.target.value)
              setAt(agentPage([e.target.value], [qs[i]?.data], hash, lastPage))
            }}
          >
            {rooms.map((r) => (
              <option key={r.project_id} value={r.project_id}>
                {name(r)}
              </option>
            ))}
          </select>
        ) : undefined
      }
      tail={
        !covers && (
          <button
            type="button"
            className="bd-drawer-full"
            aria-label="Open the pages full screen"
            title="Full screen"
            onClick={() => {
              onClose()
              openPages(shown.conv, shown.page)
            }}
          >
            <Icon name="open_in_new" size={17} />
          </button>
        )
      }
    />
  )
}

function PagesView({
  convId,
  title,
  sel,
  onSelect,
  onClose,
  drawer,
  room,
  tail,
}: {
  convId: string
  title: string
  sel: string | null
  onSelect: (page: string | null) => void
  onClose: () => void
  /** Out of the room's right edge (PagesDrawer), not over the whole app. */
  drawer?: { covers: boolean; swipe: ReturnType<typeof useDrawerSwipe> }
  /** In place of the room's name beside the page's (an agent's drawer: a choice, when it is in several rooms). */
  room?: ReactNode
  /** At the header's end (an agent's drawer: to the full screen). */
  tail?: ReactNode
}) {
  const phone = usePhone()
  const { pages, failed, put } = usePages(convId)
  const [error, setError] = useState<string | null>(null)
  const [fresh, setFresh] = useState<string | null>(null)
  const open = pages?.find((p) => p.page === sel) ?? null
  // A wide screen picks its page from a menu under the name, as a whiteboard does: the page has the width
  // (owner, 2026-11-03: 使用下拉的選單來選擇就好. 省下側邊欄的空間. 增加內容空間).
  const [menu, setMenu] = useState(false)
  const nameBtn = useRef<HTMLButtonElement>(null)
  const menuBox = useRef<HTMLDivElement>(null)
  const [menuPos, setMenuPos] = useState<CSSProperties>({})
  useLayoutEffect(() => {
    if (!menu) return
    const r = nameBtn.current?.getBoundingClientRect()
    if (r) setMenuPos({ left: Math.max(8, r.left), top: r.bottom + 6 })
  }, [menu])
  useEffect(() => {
    if (!menu) return
    const down = (e: PointerEvent) => {
      const t = e.target as Node
      if (menuBox.current?.contains(t) || nameBtn.current?.contains(t)) return
      setMenu(false)
    }
    window.addEventListener('pointerdown', down, true)
    return () => window.removeEventListener('pointerdown', down, true)
  }, [menu])
  // What was marked on the page and asked about, for the chat to carry (BoardChat quotedBody).
  const [quote, setQuote] = useState<string | null>(null)
  const ask = (text: string) => {
    setQuote(text)
    setChatOpen(true)
  }
  // The project's chat over the page (owner, 2026-11-02: 與朋友在 Project Chat 裡面協作): what is sent from it names the page.
  const { chat, chatOpen, setChatOpen, unread } = useRoomChat(convId)
  const membersQ = useQuery({ queryKey: ['proj-members', convId], queryFn: () => getProjectMembers(convId) })

  const make = () => {
    setError(null)
    createPage(convId, UNTITLED)
      .then((r) => {
        put(r.page)
        setFresh(r.page.page)
        onSelect(r.page.page)
      })
      .catch((e) => setError(failText(e)))
  }

  const list = (
    <PageList
      pages={pages}
      failed={failed}
      current={sel}
      onPick={(p) => {
        setMenu(false)
        setQuote(null)
        onSelect(p)
      }}
      onNew={() => {
        setMenu(false)
        make()
      }}
      me={chatv1SelfUser()}
      onDelete={(p) =>
        deletePage(convId, p)
          .then(() => {
            put({ page: p, del: true })
            if (sel === p) onSelect(null)
          })
          .catch((e) => setError(failText(e)))
      }
    />
  )
  const toList = phone && open && (
    <button type="button" className="pg-back" onClick={() => onSelect(null)} aria-label="Back to the pages">
      <Icon name="chevron_left" size={22} />
      <span>Pages</span>
    </button>
  )

  return (
    <div
      className={['pg-screen', drawer ? 'pg-drawer' : '', drawer?.covers ? 'pg-cover' : '', phone ? 'pg-phone' : ''].filter(Boolean).join(' ')}
      role={drawer ? 'complementary' : 'dialog'}
      aria-label={`Pages — ${title}`}
    >
      <div className="pg-top" {...drawer?.swipe}>
        {drawer ? (
          <>
            {/* The drawer's close: a chevron toward the edge it goes back into (the whiteboard drawer's). */}
            <button type="button" className="pg-back pg-drawer-close" onClick={onClose} aria-label="Close the pages" title="Close">
              <Icon name="chevron_right" size={22} />
            </button>
            {toList}
          </>
        ) : (
          toList || (
            <button type="button" className="pg-back" onClick={onClose} aria-label="Back to the chat">
              <Icon name="chevron_left" size={22} />
              <span>Back</span>
            </button>
          )
        )}
        <div className="pg-where">
          {phone ? (
            <>
              <Icon name="description" size={18} />
              <span className="pg-where-name">{open ? open.name : 'Pages'}</span>
              {room}
            </>
          ) : (
            <>
              <button
                ref={nameBtn}
                type="button"
                className={`bd-title-btn${menu ? ' on' : ''}`}
                aria-haspopup="dialog"
                aria-expanded={menu}
                title="Pages in this project"
                onClick={() => setMenu((m) => !m)}
              >
                <Icon name="description" size={18} />
                <span className="pg-where-name">{open ? open.name : 'Pages'}</span>
                <Icon name="expand_more" size={18} />
              </button>
              {room ?? <span className="pg-where-room">{title}</span>}
            </>
          )}
        </div>
        {tail}
      </div>
      {menu && !phone && (
        <div
          ref={menuBox}
          className="bd-menu pg-menu"
          style={menuPos}
          role="dialog"
          aria-label={`Pages in ${title}`}
          onKeyDown={(e) => {
            if (e.key === 'Escape') setMenu(false)
          }}
        >
          <div className="bd-menu-head">Pages in {title}</div>
          {list}
        </div>
      )}
      {error && (
        <div className="pg-error" role="alert">
          <span>{error}</span>
          <button type="button" aria-label="Dismiss" onClick={() => setError(null)}>
            <Icon name="close" size={14} />
          </button>
        </div>
      )}
      <div className="pg-body">
        {phone && !open && list}
        {open ? (
          <PagePane key={open.page} convId={convId} info={open} focusTitle={fresh === open.page} onRenamed={put} onError={setError} onAsk={ask} />
        ) : (
          !phone && (
            <div className="pg-none">
              {pages && pages.length === 0 ? (
                <>
                  <strong>No pages yet</strong>
                  <span>A page is a document everyone in this project writes together, live — and their agents too.</span>
                  <button type="button" className="pg-new-big" onClick={make}>
                    <Icon name="add" size={18} />
                    New page
                  </button>
                </>
              ) : (
                !menu && list
              )}
            </div>
          )
        )}
      </div>
      {open && (
        <button
          type="button"
          className={`bd-chat-btn pg-chat-btn${chatOpen ? ' on' : ''}`}
          title="Project chat"
          aria-label={unread ? `Project chat, ${unread} new` : 'Project chat'}
          aria-expanded={chatOpen}
          onClick={() => setChatOpen((o) => !o)}
        >
          <Icon name="chat" size={20} />
          {unread > 0 && <span className="bd-chat-badge">{unread > 9 ? '9+' : unread}</span>}
        </button>
      )}
      {open && chatOpen && (
        <BoardChat
          convId={convId}
          from={{ page: open.page, name: open.name }}
          project={title}
          agents={mentionRowsOfMembers(membersQ.data?.members ?? [], [])}
          messages={chat.messages}
          loading={chat.loading}
          hasOlder={chat.hasOlder}
          loadingOlder={chat.loadingOlder}
          loadOlder={() => void chat.loadOlder()}
          onClose={() => setChatOpen(false)}
          quote={quote}
          onQuoteDone={() => setQuote(null)}
        />
      )}
    </div>
  )
}

/** A room's pages, for the wide screen's menu and a phone's first screen: its pages in the order made, the open one marked, a new one at the top. */
function PageList({
  pages,
  failed,
  current,
  onPick,
  onNew,
  me,
  onDelete,
}: {
  pages: PageInfo[] | null
  failed: boolean
  current: string | null
  onPick: (page: string) => void
  onNew: () => void
  /** This person: a page locked to them has no bin. */
  me: string
  onDelete: (page: string) => void
}) {
  // A delete is two presses: the bin, then "Delete" on the same row.
  const [asking, setAsking] = useState<string | null>(null)
  return (
    <nav className="pg-list" aria-label="Pages in this project">
      <button type="button" className="pg-new" onClick={onNew}>
        <Icon name="add" size={18} />
        <span>New page</span>
      </button>
      {!pages && <p className="pg-list-note">{failed ? 'The pages could not be read.' : 'Reading the pages…'}</p>}
      {pages?.map((p) => (
        <div key={p.page} className={`pg-item${p.page === current ? ' on' : ''}`}>
          <button type="button" className="pg-item-open" aria-current={p.page === current ? 'page' : undefined} onClick={() => onPick(p.page)}>
            <Icon name="description" size={16} />
            <span>{p.name || UNTITLED}</span>
            {p.edit && <Icon name="lock" size={13} label="Locked" className="pg-item-lock" />}
          </button>
          {!mayChange(p, me) ? null : asking === p.page ? (
            <button
              type="button"
              className="pg-item-sure"
              onClick={() => {
                setAsking(null)
                onDelete(p.page)
              }}
              onBlur={() => setAsking(null)}
              autoFocus
            >
              Delete
            </button>
          ) : (
            <button type="button" className="pg-item-del" aria-label={`Delete ${p.name || UNTITLED}`} title="Delete" onClick={() => setAsking(p.page)}>
              <Icon name="delete" size={15} />
            </button>
          )}
        </div>
      ))}
    </nav>
  )
}

/** One page: its title (the page's name — Enter or leaving it saves) and its blocks, live. */
function PagePane({
  convId,
  info,
  focusTitle,
  onRenamed,
  onError,
  onAsk,
}: {
  convId: string
  info: PageInfo
  focusTitle: boolean
  onRenamed: (p: PageInfo) => void
  onError: (text: string) => void
  /** Some words marked and asked about (PageEditor's toolbar). */
  onAsk: (text: string) => void
}) {
  const page = usePage(convId, info.page)
  const [name, setName] = useState(info.name === UNTITLED && focusTitle ? '' : info.name)
  const editing = useRef(false)
  // Renamed elsewhere while not being typed here: the new name shows.
  useEffect(() => {
    if (!editing.current) setName(info.name)
  }, [info.name])
  const membersQ = useQuery({ queryKey: ['proj-members', convId], queryFn: () => getProjectMembers(convId) })
  const nameOf = (user: string, agent?: string): string => {
    const rows = membersQ.data?.members ?? []
    if (agent) return rows.find((m) => m.kind === 'agent' && m.channel_hash === agent)?.label ?? 'An agent'
    const h = rows.find((m) => m.kind !== 'agent' && m.user === user)
    return h?.label ?? h?.email ?? 'Someone'
  }

  const may = mayChange(info, chatv1SelfUser())
  const saveLock = (edit: EditMode, editors?: string[]) => setPageLock(convId, info.page, edit, editors).then((r) => onRenamed(r.page))

  const saveName = () => {
    editing.current = false
    if (!may) return
    const next = name.trim().slice(0, 80) || UNTITLED
    if (next === info.name) {
      setName(next)
      return
    }
    renamePage(convId, info.page, next)
      .then((r) => onRenamed(r.page))
      .catch((e) => {
        setName(info.name)
        onError(failText(e))
      })
  }

  return (
    <div className="pg-pane">
      <div className="pg-doc">
        <div className="pg-state">
          <EditLock convId={convId} what="page" info={info} onSave={saveLock} className="pg-edit-lock" />
          <span className={`pg-saving pg-${page.saving}`} aria-live="polite">
            {page.saving === 'saving' ? 'Saving…' : page.saving === 'offline' ? 'Not saved yet — retrying' : page.status === 'ready' ? 'Saved' : ''}
          </span>
        </div>
        <input
          className="pg-title"
          aria-label="Page title"
          placeholder={UNTITLED}
          value={name}
          readOnly={!may}
          maxLength={80}
          autoFocus={focusTitle}
          onFocus={() => (editing.current = true)}
          onChange={(e) => setName(e.target.value)}
          onBlur={saveName}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault()
              ;(e.target as HTMLInputElement).blur()
            }
          }}
        />
        {page.error && (
          <div className="pg-error pg-error-in" role="alert">
            <span>{page.error}</span>
            <button type="button" aria-label="Dismiss" onClick={page.clearError}>
              <Icon name="close" size={14} />
            </button>
          </div>
        )}
        {page.status === 'ready' ? (
          <Suspense fallback={<p className="pg-note">Opening the editor…</p>}>
            <PageEditor rows={page.els} apply={(ops) => page.apply(ops, { history: 'none', live: true })} editable={may} onAsk={onAsk} conv={convId} />
          </Suspense>
        ) : (
          <p className="pg-note">
            {page.status === 'missing' ? 'This page is no longer in this project.' : page.status === 'error' ? 'The page could not be read.' : 'Opening the page…'}
          </p>
        )}
        {page.activity && (
          <div className={`pg-activity${page.activity.via ? ' pg-by-agent' : ''}`} role="status">
            <Icon name={page.activity.via ? 'smart_toy' : 'group'} size={16} />
            <span>{activityText(page.activity, nameOf)}</span>
            <button type="button" aria-label="Close" onClick={page.clearActivity}>
              <Icon name="close" size={14} />
            </button>
          </div>
        )}
      </div>
    </div>
  )
}
